/**
 * Accessibility patches for puckerbuttpeppercompany.com
 * Reformation theme by Fuel Themes
 *
 * Each patch addresses a specific WCAG violation that couldn't be fixed
 * directly in the source Liquid templates. Most apply to third-party
 * libraries (Flickity, noUiSlider, Klaviyo) or apps where we don't
 * control the markup.
 *
 * Patches included:
 *   1. Klaviyo app embed dialog labeler
 *   2. External link "opens in new window" labels
 *   3. Flickity off-screen slide focus prevention
 *   4. noUiSlider handle accessible labels
 */

(function() {
  'use strict';

  // ===========================================================================
  // PATCH 1: Klaviyo app embed dialog
  // The Klaviyo email capture popup renders a [role="dialog"] without
  // aria-label or aria-labelledby. Add a label when the dialog appears.
  // ===========================================================================
  (function patchAppEmbedDialog() {
    const patchDialog = () => {
      const dialog = document.querySelector(
        '[id^="app-embed-container-"] [role="dialog"]:not([aria-label]):not([aria-labelledby])'
      );
      if (dialog) {
        dialog.setAttribute('aria-label', 'Notification dialog');
        return true;
      }
      return false;
    };

    if (!patchDialog()) {
      const observer = new MutationObserver(() => {
        if (patchDialog()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  })();

  // ===========================================================================
  // PATCH 2: External links — add "opens in new window" to accessible name
  // WCAG requires that links opening in new windows warn the user.
  // ===========================================================================
  (function patchExternalLinks() {
    const labelExternalLinks = () => {
      document.querySelectorAll('a[target="_blank"]:not([data-external-labeled])').forEach(link => {
        const existingLabel = link.getAttribute('aria-label');
        if (existingLabel && existingLabel.toLowerCase().includes('new window')) {
          link.setAttribute('data-external-labeled', 'true');
          return;
        }
        const linkText = (existingLabel || link.textContent || '').trim();
        const newLabel = linkText
          ? `${linkText} (opens in new window)`
          : 'Opens in new window';
        link.setAttribute('aria-label', newLabel);
        link.setAttribute('data-external-labeled', 'true');
      });
    };

    const init = () => {
      labelExternalLinks();
      const observer = new MutationObserver(labelExternalLinks);
      observer.observe(document.body, { childList: true, subtree: true });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  })();

  // ===========================================================================
  // PATCH 3: Flickity off-screen slides — remove focusable descendants
  // Flickity hides slides with aria-hidden but leaves their links and buttons
  // in the tab order, creating dozens of invisible focus stops.
  // ===========================================================================
  (function patchFlickitySlides() {
    const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const patchSlide = (slide) => {
      const isHidden = slide.getAttribute('aria-hidden') === 'true';
      slide.querySelectorAll(FOCUSABLE).forEach(el => {
        if (isHidden) {
          if (el.hasAttribute('tabindex') && !el.hasAttribute('data-original-tabindex')) {
            el.setAttribute('data-original-tabindex', el.getAttribute('tabindex'));
          }
          el.setAttribute('tabindex', '-1');
          el.setAttribute('data-flickity-patched', 'true');
        } else {
          if (el.hasAttribute('data-original-tabindex')) {
            el.setAttribute('tabindex', el.getAttribute('data-original-tabindex'));
            el.removeAttribute('data-original-tabindex');
          } else if (el.getAttribute('tabindex') === '-1' && el.hasAttribute('data-flickity-patched')) {
            el.removeAttribute('tabindex');
          }
          el.removeAttribute('data-flickity-patched');
        }
      });
    };

    const patchAllSlides = () => {
      document.querySelectorAll('.carousel__slide, .flickity-slider > *').forEach(patchSlide);
    };

    const runInitialPatch = () => {
      setTimeout(patchAllSlides, 100);
      setTimeout(patchAllSlides, 500);
      setTimeout(patchAllSlides, 1500);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runInitialPatch);
    } else {
      runInitialPatch();
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
          const target = mutation.target;
          if (target.classList.contains('carousel__slide') ||
              (target.parentElement && target.parentElement.classList.contains('flickity-slider'))) {
            patchSlide(target);
          }
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      subtree: true
    });
  })();

  // ===========================================================================
  // PATCH 4: noUiSlider handles — add accessible labels
  // The price filter slider creates role="slider" elements via JavaScript
  // with no aria-label. Add contextual labels after initialization.
  // ===========================================================================
  (function patchSliderHandles() {
    const labelSliderHandles = () => {
      document.querySelectorAll('.noUi-handle').forEach(handle => {
        if (handle.hasAttribute('aria-label')) return;

        const isLower = handle.classList.contains('noUi-handle-lower');
        const isUpper = handle.classList.contains('noUi-handle-upper');

        const filterContainer = handle.closest('.thb-filter');
        const titleEl = filterContainer ? filterContainer.querySelector('.thb-filter-title') : null;
        const facetName = titleEl
          ? titleEl.textContent.trim().replace(/^\s*\W*\s*/, '')
          : 'price';

        if (isLower) {
          handle.setAttribute('aria-label', 'Minimum ' + facetName);
        } else if (isUpper) {
          handle.setAttribute('aria-label', 'Maximum ' + facetName);
        } else {
          handle.setAttribute('aria-label', facetName);
        }
      });
    };

    const runWithDelay = () => {
      setTimeout(labelSliderHandles, 100);
      setTimeout(labelSliderHandles, 500);
      setTimeout(labelSliderHandles, 1500);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', runWithDelay);
    } else {
      runWithDelay();
    }

    const observer = new MutationObserver(labelSliderHandles);
    observer.observe(document.body, { childList: true, subtree: true });
  })();

})();